import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material';
import useSWR from 'swr';
import { mainColors, secondaryColors } from '../../theme/themeConfig';
import PartnerPortalApi from '../../api/partnerPortal';
import type { PartnerProfile, PortalSkill } from '../../api/partnerPortal';

const PortalProfile: React.FC = function PortalProfile() {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<PartnerProfile>>({});
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);

  const {
    data: profile,
    error,
    mutate
  } = useSWR('portal-profile', PartnerPortalApi.getProfile);

  const { data: availableSkills } = useSWR(
    'portal-available-skills',
    PartnerPortalApi.getAvailableSkills
  );

  const { data: partnerSkills, mutate: mutateSkills } = useSWR(
    'portal-skills',
    PartnerPortalApi.getPartnerSkills
  );

  useEffect(() => {
    if (profile) {
      setForm({
        addressLine1: profile.addressLine1 || '',
        city: profile.city || '',
        contactFirstName: profile.contactFirstName || '',
        contactLastName: profile.contactLastName || '',
        contactPhone: profile.contactPhone || '',
        country: profile.country || '',
        description: profile.description || '',
        state: profile.state || '',
        zipCode: profile.zipCode || ''
      });
    }
  }, [profile]);

  useEffect(() => {
    if (partnerSkills) {
      setSelectedSkillIds(partnerSkills.map((s: PortalSkill) => s.id));
    }
  }, [partnerSkills]);

  const onChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await PartnerPortalApi.updateProfile(form);
      await PartnerPortalApi.updateSkills(selectedSkillIds);
      mutate();
      mutateSkills();
      setEditing(false);
    } catch (e) {
      // handled
    } finally {
      setSaving(false);
    }
  };

  const toggleSkill = (skillId: string) => {
    setSelectedSkillIds((prev) =>
      prev.includes(skillId)
        ? prev.filter((id) => id !== skillId)
        : [...prev, skillId]
    );
  };

  const loading = !profile && !error;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">Failed to load profile.</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Box>
          <Typography
            sx={{
              color: secondaryColors.SECONDARY,
              fontSize: '22px',
              fontWeight: 600
            }}
          >
            {profile?.companyName}
          </Typography>
          <Chip
            color={profile?.status === 'active' ? 'success' : 'warning'}
            label={(profile?.status || '').replace(/_/g, ' ')}
            size="small"
            sx={{ mt: 0.5 }}
          />
        </Box>
        {!editing ? (
          <Button
            onClick={() => setEditing(true)}
            sx={{
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'none'
            }}
            variant="outlined"
          >
            Edit profile
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => {
                setEditing(false);
                if (profile) setForm({ ...profile });
              }}
              sx={{
                borderRadius: '8px',
                fontSize: '13px',
                textTransform: 'none'
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              disabled={saving}
              onClick={handleSave}
              sx={{
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none'
              }}
              variant="contained"
            >
              {saving ? <CircularProgress color="inherit" size={18} /> : 'Save'}
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            disabled={!editing}
            label="First name"
            onChange={(e) => onChange('contactFirstName', e.target.value)}
            size="small"
            value={form.contactFirstName || ''}
          />
          <TextField
            fullWidth
            disabled={!editing}
            label="Last name"
            onChange={(e) => onChange('contactLastName', e.target.value)}
            size="small"
            value={form.contactLastName || ''}
          />
        </Box>
        <TextField
          disabled
          fullWidth
          label="Email"
          size="small"
          value={profile?.contactEmail || ''}
        />
        <TextField
          fullWidth
          disabled={!editing}
          label="Phone"
          onChange={(e) => onChange('contactPhone', e.target.value)}
          size="small"
          value={form.contactPhone || ''}
        />
        <TextField
          fullWidth
          disabled={!editing}
          label="Address"
          onChange={(e) => onChange('addressLine1', e.target.value)}
          size="small"
          value={form.addressLine1 || ''}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            disabled={!editing}
            label="City"
            onChange={(e) => onChange('city', e.target.value)}
            size="small"
            value={form.city || ''}
          />
          <TextField
            fullWidth
            disabled={!editing}
            label="State"
            onChange={(e) => onChange('state', e.target.value)}
            size="small"
            value={form.state || ''}
          />
          <TextField
            disabled={!editing}
            label="Zip"
            onChange={(e) => onChange('zipCode', e.target.value)}
            size="small"
            sx={{ width: 140 }}
            value={form.zipCode || ''}
          />
        </Box>
        <TextField
          fullWidth
          multiline
          disabled={!editing}
          label="Company description"
          onChange={(e) => onChange('description', e.target.value)}
          rows={3}
          size="small"
          value={form.description || ''}
        />

        <Box sx={{ mt: 1 }}>
          <Typography
            sx={{
              color: secondaryColors.SECONDARY,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '.5px',
              mb: 1,
              textTransform: 'uppercase'
            }}
          >
            Skills &amp; capabilities — select the services your company can
            perform
          </Typography>
          {availableSkills && availableSkills.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availableSkills.map((skill: PortalSkill) => {
                const selected = selectedSkillIds.includes(skill.id);
                return (
                  <Chip
                    clickable={editing}
                    color={selected ? 'primary' : 'default'}
                    disabled={!editing}
                    key={skill.id}
                    label={skill.name}
                    onClick={() => {
                      if (editing) toggleSkill(skill.id);
                    }}
                    size="small"
                    variant={selected ? 'filled' : 'outlined'}
                  />
                );
              })}
            </Box>
          ) : (
            <Typography color="text.secondary" variant="body2">
              No skills configured by your host company yet.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PortalProfile;
